import { useState } from 'react';

import { IconArrowDown, IconArrowUp, IconSort } from '@iwoca/icons';
import { Button, Pagination, Spinner, Table } from '@iwoca/orion';
import { clsx } from 'clsx';

import { ApprovedLimitCell } from './cells/ApprovedLimitCell';
import { CompanyNameCell } from './cells/CompanyNameCell';
import { LatestPaymentCell } from './cells/LatestPaymentCell';
import { OnboardingProgressCell } from './cells/OnboardingProgressCell';
import { ToSpendCell } from './cells/ToSpendCell';
import { CustomersMobileTable } from './CustomersMobileTable';
import { HeaderInfoCell } from './HeaderInfoCell';
import { SortableHeaderCell } from './SortableHeaderCell';
import { LoadingModal } from '../../../../components/LoadingModal/LoadingModal';
import { formatDate } from '../../../Fees/RateContextHeader/RateContextHeader';
import {
  ColumnConfig,
  CustomerRow,
  OrderingParam,
  SORT_KEYS,
  SortKey,
  TabConfig,
} from '../../CustomersV2.types';
import { useCustomersQuery } from '../../hooks/useCustomersQuery';
import { useInfiniteScrollSentinel } from '../../hooks/useInfiniteScrollSentinel';
import { useIsMobileTableLayout } from '../../hooks/useIsMobileTableLayout';
import { useProfileBuyer } from '../../hooks/useProfileBuyer';
import { TableSettings } from '../../hooks/useTableSettings';
import { statusLabel } from '../../statusLabel';
import {
  trackCustomerProfileOpened,
  trackInfoModalOpened,
  trackSortChanged,
} from '../../tracking';
import { CustomerProfileModal } from '../CustomerProfileModal/CustomerProfileModal';
import { sortFieldHeading } from '../SortDrawer/buildSortOptions';
import { SortDrawer } from '../SortDrawer/SortDrawer';
import { StatusInfoModal } from '../StatusInfoModal/StatusInfoModal';

/** Splits the ordering the rows are in into its field and direction, or null when the field has no sort config. */
const parseSort = (
  sort: OrderingParam,
): { sortKey: SortKey; direction: 'ascending' | 'descending' } | null => {
  // Matched against the key whitelist the way SortableHeaderCell compares, so
  // the field is a SortKey by derivation rather than by assertion — nothing
  // downstream can be handed a key the sort config has no entry for.
  const sortKey = SORT_KEYS.find((key) => sort === key || sort === `-${key}`);
  if (sortKey === undefined) return null;
  return {
    sortKey,
    direction: sort === sortKey ? 'ascending' : 'descending',
  };
};

const renderCell = (
  columnId: string,
  row: CustomerRow,
  onCompanyClick: (buyerStateKey: string) => void,
): React.ReactNode => {
  switch (columnId) {
    case 'date':
      return formatDate(row.earliest_interacted_date, 'short');
    case 'company':
      return <CompanyNameCell row={row} onCompanyClick={onCompanyClick} />;
    case 'approvedLimit':
      return <ApprovedLimitCell row={row} />;
    case 'onboardingProgress':
      return <OnboardingProgressCell row={row} />;
    case 'status':
      return statusLabel(row);
    case 'toSpend':
      return <ToSpendCell row={row} />;
    case 'latestPayment':
      return <LatestPaymentCell row={row} />;
    default:
      return null;
  }
};

export const CustomersTable = ({
  config,
  visibleColumns,
  settings,
  settingsControl,
}: {
  config: TabConfig;
  visibleColumns: ColumnConfig[];
  settings: TableSettings;
  /** Rendered beside the Sort button, and only in the mobile layout — the desktop toolbar owns its own copy. */
  settingsControl?: React.ReactNode;
}) => {
  const { isMobile } = useIsMobileTableLayout();
  const {
    rows,
    meta,
    loading,
    state,
    setPage,
    setSort,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCustomersQuery(config, settings, { infinite: isMobile });
  const { selectedRow, openProfile, closeProfile } = useProfileBuyer({
    rows,
    loading,
    tab: config.path,
  });
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const totalPages = meta?.total_pages ?? 0;
  const appliedSort = parseSort(state.sort);

  // Both sort controls route through here, so the event is emitted once for the
  // pair. `null` means the header cycled back to no explicit sort, which resolves
  // to the tab's default — report what the table will actually be ordered by,
  // not the absence.
  const applySort = (
    value: OrderingParam | null,
    source: 'header' | 'drawer',
  ) => {
    trackSortChanged({
      tab: config.path,
      ordering: value ?? config.defaultSort,
      source,
    });
    setSort(value);
  };

  const openProfileFromTable = (buyerStateKey: string) => {
    const row = rows.find(
      (candidate) => candidate.buyer_state_key === buyerStateKey,
    );
    // The status properties come off the row, so a click with no matching row
    // would have nothing to report. Only the cells of rendered rows can call
    // this, so it can't be missing in practice.
    if (row)
      trackCustomerProfileOpened({ tab: config.path, source: 'table', row });
    openProfile(buyerStateKey);
  };

  const openStatusInfo = () => {
    trackInfoModalOpened({ tab: config.path });
    setIsStatusModalOpen(true);
  };

  // Disabled while a page is in flight so one crossing can't queue several
  // fetches, and once exhausted so the spinner and observer both stand down.
  const sentinelRef = useInfiniteScrollSentinel({
    enabled: isMobile && hasNextPage && !isFetchingNextPage,
    onIntersect: fetchNextPage,
    loadedCount: rows.length,
  });

  return (
    <div className="w-full">
      <LoadingModal
        key={state.page}
        // Desktop only: the mobile layout draws its own spinner in place of the
        // rows, so a modal over it would be a second indicator for one fetch.
        isOpen={loading && !isMobile}
        title="We're pulling together the latest customer updates"
        subText="You've got a lot of great data here - give us a moment while we bring it all together."
      />

      {isMobile ? (
        <div className="flex flex-col gap-l">
          <div className="flex items-center gap-l">
            {settingsControl}
            <Button
              variant="secondary-alt"
              size="small"
              className="flex items-center gap-s"
              onClick={() => setIsSortDrawerOpen(true)}
            >
              <IconSort height={14} width={14} />
              Sort
            </Button>
          </div>
          {appliedSort && (
            // The stacked layout has no header row to carry `aria-sort`, so the
            // field and direction a sighted user reads off the arrow are spelled
            // out instead. A live region because this is the only confirmation
            // that applying the sort drawer took effect.
            <div
              role="status"
              className="flex items-center gap-s text-s font-bold text-secondary-40"
            >
              <span className="sr-only">
                {`Sorted by ${sortFieldHeading(appliedSort.sortKey)}, ${appliedSort.direction}`}
              </span>
              {appliedSort.direction === 'ascending' ? (
                <IconArrowUp height={14} width={14} aria-hidden />
              ) : (
                <IconArrowDown height={14} width={14} aria-hidden />
              )}
              <span aria-hidden>{sortFieldHeading(appliedSort.sortKey)}</span>
            </div>
          )}
          <CustomersMobileTable
            rows={rows}
            loading={loading}
            config={config}
            settings={settings}
            onCompanyClick={openProfileFromTable}
            onInfoClick={openStatusInfo}
          />
          {/* Kept mounted while fetching so the observer still has an element to
              watch, and so the spinner replaces it in the same flow position.
              A live region because scrolling is the only paging mechanism here —
              nothing else reports that the list grew. */}
          {hasNextPage && (
            <div
              ref={sentinelRef}
              data-testid="customers-scroll-sentinel"
              role="status"
              className="flex w-full justify-center"
            >
              <span className="sr-only">
                {isFetchingNextPage
                  ? 'Loading more customers'
                  : `Showing ${rows.length} of ${meta?.count ?? rows.length} customers. Scroll for more.`}
              </span>
              {/* Orion's Spinner exposes its test id as `dataTestId`; a
                  `data-testid` prop is dropped rather than forwarded. */}
              {isFetchingNextPage && (
                <Spinner dataTestId="customers-next-page-spinner" />
              )}
            </div>
          )}
          <SortDrawer
            config={config}
            activeSort={state.sort}
            isOpen={isSortDrawerOpen}
            onClose={() => setIsSortDrawerOpen(false)}
            onSort={(value) => applySort(value, 'drawer')}
          />
        </div>
      ) : (
        <Table.Root className="w-full">
          <Table.Header>
            {/* Header cells stretch to the row, so the height lives here rather
                than on each of the three kinds of header cell below. Orion
                divides the header from the body in the same tone it uses
                between body rows, which leaves the two reading as one run. */}
            <Table.Row className="min-h-[64px] [&_th]:!border-b-primary-90">
              {visibleColumns.map((col) =>
                col.sortKey ? (
                  <SortableHeaderCell
                    key={col.id}
                    header={col.header}
                    sortKey={col.sortKey}
                    explicitSort={state.explicitSort}
                    defaultSort={config.defaultSort}
                    className={col.width}
                    onSort={(value) => applySort(value, 'header')}
                  />
                ) : col.infoModal ? (
                  <HeaderInfoCell
                    key={col.id}
                    header={col.header}
                    className={col.width}
                    onInfoClick={openStatusInfo}
                  />
                ) : (
                  <Table.HeaderCell key={col.id} className={col.width}>
                    {col.header}
                  </Table.HeaderCell>
                ),
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => {
              const isDeclined = row.application_stage.value === 'declined';
              return (
                <Table.Row key={row.buyer_state_key}>
                  {visibleColumns.map((col) => (
                    <Table.Cell
                      key={col.id}
                      className={clsx(
                        '!items-center font-med text-primary-10',
                        // Date and company carry a single value at the body
                        // size. Every other column pairs its value with a note
                        // or a second line, which sit a step down.
                        col.id !== 'date' && col.id !== 'company' && '!text-s',
                        isDeclined && '!bg-core-95',
                        col.width,
                      )}
                    >
                      {renderCell(col.id, row, openProfileFromTable)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      )}

      {/* Desktop only: the mobile layout advances by scrolling, and a page
          control would contradict the accumulated list. */}
      {!isMobile && totalPages > 1 && (
        <Pagination
          className="mx-auto mt-2xl"
          page={state.page}
          count={totalPages}
          onPageChange={(nextPage) => {
            window.scrollTo({ top: 0 });
            setPage(nextPage);
          }}
        />
      )}

      <CustomerProfileModal row={selectedRow} onClose={closeProfile} />

      <StatusInfoModal
        tabKey={config.key}
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
      />
    </div>
  );
};
